import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line } from 'recharts';
import { getRevenueData } from '../../data/mockApi';

const RevenueChart = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const result = await getRevenueData();
      setData(result);
      setLoading(false);
    };
    fetchData();
  }, []);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#111916] border border-[#243129] rounded-lg p-3 shadow-xl">
          <p className="text-xs font-mono text-[#6b8772] mb-2">{label}</p>
          {payload.map((item, index) => (
            <div key={index} className="flex items-center gap-2 text-sm font-mono">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-[#a5c4ad]">{item.name === 'revenue' ? 'Revenue' : 'Orders'}:</span>
              <span className="text-[#e8f5e9] font-medium">
                {item.name === 'revenue' ? `N$${item.value.toLocaleString()}` : item.value}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.4 }}
      className="bg-[#111916] rounded-xl border border-[#243129]"
    >
      <div className="p-5 border-b border-[#243129]">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-semibold text-[#e8f5e9]">Revenue Trend</h3>
            <p className="text-xs font-mono text-[#6b8772] mt-1">7-day performance overview</p>
          </div>
          <div className="flex items-center gap-4 text-xs font-mono">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-lime-400 rounded-full"></div>
              <span className="text-[#a5c4ad]">Revenue</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-sky-400 rounded-full"></div>
              <span className="text-[#a5c4ad]">Orders</span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-5">
        <div className="h-64">
          {loading ? (
            <div className="flex items-center justify-center h-full text-[#6b8772] font-mono text-sm">
              Loading chart data...
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#84cc16" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#84cc16" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#1c2822"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#6b8772', fontSize: 11, fontFamily: "'IBM Plex Mono', monospace" }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#6b8772', fontSize: 11, fontFamily: "'IBM Plex Mono', monospace" }}
                  tickFormatter={(value) => `N$${value / 1000}k`}
                  dx={-10}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#84cc16"
                  strokeWidth={2}
                  fill="url(#colorRevenue)"
                  dot={false}
                  activeDot={{
                    r: 5,
                    fill: '#84cc16',
                    stroke: '#111916',
                    strokeWidth: 2
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="orders"
                  stroke="#38bdf8"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{
                    r: 5,
                    fill: '#38bdf8',
                    stroke: '#111916',
                    strokeWidth: 2
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default RevenueChart;
